import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalConfig, loginRequest } from './authConfig';
import { theme } from './theme';
import ApiProvider from './providers/ApiProvider';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Editor from './pages/Editor';
import Publish from './pages/Publish';
import History from './pages/History';
import EditRelease from './pages/EditRelease';
import Subscribers from './pages/Subscribers';
import QueryFilters from './pages/settings/QueryFilters';
import ModuleMappings from './pages/settings/ModuleMappings';
import ReleaseTypes from './pages/settings/ReleaseTypes';
import Terms from './pages/settings/Terms';
import PublicReleases from './pages/public/Releases';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

// Loading component while authenticating
function Loading() {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div>Loading...</div>
    </div>
  );
}

// Error component for auth errors
function AuthError({ error }: { error: Error | null }) {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontFamily: 'Inter, sans-serif',
      gap: '16px',
    }}>
      <h2>Authentication Error</h2>
      <p>{error?.message || 'An error occurred during authentication'}</p>
      <button 
        onClick={() => window.location.reload()}
        style={{
          padding: '8px 16px',
          backgroundColor: '#00838f',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Try Again
      </button>
    </div>
  );
}

// Protected routes wrapper
function ProtectedRoutes() {
  return (
    <MsalAuthenticationTemplate 
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={Loading}
      errorComponent={AuthError}
    >
      <ApiProvider>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="editor/:id" element={<Editor />} />
            <Route path="publish" element={<Publish />} />
            <Route path="history" element={<History />} />
            <Route path="releases/:id/edit" element={<EditRelease />} />
            <Route path="subscribers" element={<Subscribers />} />
            <Route path="settings/query-filters" element={<QueryFilters />} />
            <Route path="settings/module-mappings" element={<ModuleMappings />} />
            <Route path="settings/release-types" element={<ReleaseTypes />} />
            <Route path="settings/terms" element={<Terms />} />
          </Route>
        </Routes>
      </ApiProvider>
    </MsalAuthenticationTemplate>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <Routes>
              {/* Public routes - no auth required */}
              <Route path="/releases" element={<PublicReleases />} />
              <Route path="/releases/:id" element={<PublicReleases />} />
              
              {/* Protected routes */}
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </MsalProvider>
  );
}
